import { marked } from 'marked';

/**
 * <lunaria-management-chat> Web Component — Phase 2
 *
 * Self-contained AI management chat: text, file attachments, streaming.
 * Voice (PTT + full-duplex) is Phase 4-5.
 *
 * Session selection is external (uri-router pattern in view-menu).
 * This component receives its session via the `session-label` attribute.
 *
 * @element lunaria-management-chat
 * @attr {string} property-id - Property UUID (required)
 * @attr {string} session-label - Active session label (reactive)
 * @fires chat:message-sent - {text, files[]} - user sent a message
 * @fires chat:session-loaded - {label, messageCount}
 * @fires chat:error - {error}
 */
class LunariaManagementChat extends HTMLElement {
  static get observedAttributes() {
    return ['property-id', 'session-label'];
  }

  constructor() {
    super();
    this._streamBubble = null;
    this._streamBuffer = '';
    this._initialized = false;
    this._pendingFiles = []; // [{name, content (base64), mime_type, size, previewUrl?}]
    // Prompt history (Issue #296): up/down to recall prior prompts.
    // Persisted per (property-id, session-label) in localStorage so it survives reload.
    this._messageHistory = [];
    this._historyIndex = null;     // null = editing fresh draft; int = navigating history
    this._draftBeforeHistory = ''; // saved when user first steps into history
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._render();
    this._bindEvents();
    this._loadHistory();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this._initialized) return;
    if (name === 'session-label' && newValue !== oldValue) {
      this._onSessionChanged(newValue);
    }
  }

  disconnectedCallback() {
    // Clean up preview URLs
    this._pendingFiles.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
  }

  // ── Public API ────────────────────────────────────────────────

  /** Add a user or assistant message to the chat */
  addMessage(role, text) {
    const container = this._messagesEl;
    if (!container) return;

    const welcome = container.querySelector('.lmc-welcome');
    if (welcome) welcome.remove();

    // Strip [Context: ...] and [Attachments: ...] blocks from display
    const displayText = text
      .replace(/\n*\[Context:[^\]]*\]/g, '')
      .replace(/\n*\[Attachments:[\s\S]*?\]/g, '')
      .trim();
    if (!displayText) return;

    const msg = document.createElement('div');
    msg.className = `lmc-message ${role}`;
    const content = role === 'assistant' ? this._renderMarkdown(displayText) : this._escapeHtml(displayText);
    msg.innerHTML = `<div class="lmc-bubble">${content}</div>`;
    container.appendChild(msg);
    container.scrollTop = container.scrollHeight;
  }

  /** Add a user message bubble with optional file previews */
  addUserMessage(text, files, options) {
    const container = this._messagesEl;
    if (!container) return;

    const welcome = container.querySelector('.lmc-welcome');
    if (welcome) welcome.remove();

    const msg = document.createElement('div');
    msg.className = 'lmc-message user';

    let html = '';

    // Voice indicator
    if (options && options.voice) {
      html += '<div class="lmc-voice-badge">🎤 Voice</div>';
    }

    // Show attached file thumbnails/cards above the text
    if (files && files.length > 0) {
      html += '<div class="lmc-attached-files">';
      files.forEach(f => {
        if (f.mime_type && f.mime_type.startsWith('image/') && f.previewUrl) {
          html += `<div class="lmc-file-preview lmc-file-image" data-open-url="${f.previewUrl}">
            <img src="${f.previewUrl}" alt="${this._escapeHtml(f.name)}" />
            <span class="lmc-file-name">${this._escapeHtml(f.name)}</span>
          </div>`;
        } else {
          const icon = this._fileIcon(f.mime_type);
          html += `<div class="lmc-file-preview lmc-file-doc">
            <span class="lmc-file-icon">${icon}</span>
            <span class="lmc-file-name">${this._escapeHtml(f.name)}</span>
            <span class="lmc-file-size">${this._formatSize(f.size)}</span>
          </div>`;
        }
      });
      html += '</div>';
    }

    if (text) {
      html += `<div class="lmc-bubble">${this._escapeHtml(text)}</div>`;
    }

    msg.innerHTML = html;
    container.appendChild(msg);

    // Double-click file previews to open in new tab
    msg.querySelectorAll('[data-open-url]').forEach(el => {
      el.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        window.open(el.dataset.openUrl, '_blank');
      });
    });

    container.scrollTop = container.scrollHeight;
  }

  /** Add a system/stage message (centered, muted, no bubble) */
  addSystemMessage(text) {
    const container = this._messagesEl;
    if (!container) return;

    const msg = document.createElement('div');
    msg.className = 'lmc-message system';
    msg.textContent = text;
    container.appendChild(msg);
    container.scrollTop = container.scrollHeight;
  }

  showTyping(show) {
    const container = this._messagesEl;
    if (!container) return;

    const existing = container.querySelector('.lmc-typing');
    if (existing) existing.remove();

    if (show) {
      const indicator = document.createElement('div');
      indicator.className = 'lmc-message assistant lmc-typing';
      indicator.innerHTML = '<div class="lmc-typing-dots"><span></span><span></span><span></span></div>';
      container.appendChild(indicator);
      container.scrollTop = container.scrollHeight;
    }
  }

  handleStreamChunk(content) {
    this.showTyping(false);
    const container = this._messagesEl;
    if (!container) return;

    if (!this._streamBubble) {
      const welcome = container.querySelector('.lmc-welcome');
      if (welcome) welcome.remove();

      const msg = document.createElement('div');
      msg.className = 'lmc-message assistant';
      msg.innerHTML = '<div class="lmc-bubble lmc-streaming"></div>';
      container.appendChild(msg);
      this._streamBubble = msg.querySelector('.lmc-streaming');
      this._streamBuffer = '';
    }

    this._streamBuffer += content;
    this._streamBubble.innerHTML = this._renderMarkdown(this._streamBuffer);
    container.scrollTop = container.scrollHeight;
  }

  handleStreamDone(fullContent) {
    this.showTyping(false);
    const text = fullContent || this._streamBuffer || '';

    if (this._streamBubble) {
      this._streamBubble.innerHTML = this._renderMarkdown(text);
      this._streamBubble.classList.remove('lmc-streaming');
      this._streamBubble = null;
    } else {
      this.addMessage('assistant', text);
    }

    this._streamBuffer = '';
  }

  handleStreamError(error) {
    this.showTyping(false);
    this.addMessage('assistant', `Error: ${error || 'Unknown error'}`);
    this._streamBubble = null;
    this._streamBuffer = '';
  }

  renderHistory(messages) {
    const container = this._messagesEl;
    if (!container) return;
    container.innerHTML = '';

    if (!messages || !messages.length) {
      this._showWelcome();
      return;
    }

    const propertyId = this.getAttribute('property-id');

    messages.forEach(m => {
      const role = m.role === 'user' ? 'user' : 'assistant';
      const text = m.content || m.text || '';

      // User messages may have file attachments from history
      if (role === 'user' && m.files && m.files.length > 0) {
        // Build file objects with real URLs for the upload proxy
        const files = m.files.map(f => {
          const isImage = f.mime_type && f.mime_type.startsWith('image/');
          const name = f.path ? f.path.split('/').pop() : f.name;
          return {
            name: f.name,
            mime_type: f.mime_type,
            previewUrl: isImage && propertyId ? `/api/ai/chat/${propertyId}/uploads/${name}` : null,
            size: f.size || 0,
          };
        });
        this.addUserMessage(text, files, m.voice ? { voice: true } : null);
      } else {
        this.addMessage(role, text);
      }
    });
  }

  clearChat(title) {
    const container = this._messagesEl;
    if (!container) return;
    container.innerHTML = '';
    this._showWelcome(title || 'Cleared');
  }

  showWelcomeState() {
    const container = this._messagesEl;
    if (!container) return;
    container.innerHTML = '';
    this._showWelcome('AI Assistant', 'Select or create a session above to start chatting.');
    this._hideInput();
  }

  showInput() {
    const inputArea = this.querySelector('.lmc-input-area');
    if (inputArea) inputArea.hidden = false;
  }

  getInputText() {
    const input = this.querySelector('.lmc-input');
    return input ? input.value.trim() : '';
  }

  clearInput() {
    const input = this.querySelector('.lmc-input');
    if (input) input.value = '';
  }

  /** Get pending files (to include in WS message) and clear the queue */
  flushPendingFiles() {
    const files = this._pendingFiles.slice();
    this._pendingFiles = [];
    this._renderAttachmentBar();
    return files;
  }

  // ── Private: File Handling ───────────────────────────────────

  /**
   * Capture the full page as a screenshot and add it to pending attachments.
   * Also accessible programmatically by the agent (M7/M8 prep).
   * M7 note: agent should also receive window.location.href for pane navigation context.
   */
  async captureScreenshot() {
    this.addSystemMessage('📸 Capturing screenshot...');
    try {
      // Dynamically load html2canvas if not already present
      // Use native browser screenshot via offscreen canvas API if available,
      // otherwise fall back to html2canvas with foreignObjectRendering which
      // bypasses CSS parsing (avoids "unsupported color function" errors).
      let canvas;
      try {
        if (!window.html2canvas) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            // Use latest version with better CSS support
            s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
            s.onload = resolve;
            s.onerror = reject;
            document.head.appendChild(s);
          });
        }
        // foreignObjectRendering uses SVG foreignObject + browser rendering engine,
        // bypassing html2canvas's own CSS parser — avoids color() parse errors
        canvas = await window.html2canvas(document.body, {
          scale: 1,
          useCORS: true,
          logging: false,
          allowTaint: true,
          foreignObjectRendering: true,
        });
      } catch (e1) {
        // foreignObjectRendering may fail in some browsers — fall back to standard render
        console.warn('[LMC] foreignObjectRendering failed, trying standard:', e1.message);
        canvas = await window.html2canvas(document.body, {
          scale: 1,
          logging: false,
          allowTaint: true,
          foreignObjectRendering: false,
        });
      }

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Canvas toBlob returned null');

      await this._addScreenshotEntry(blob);

      // Remove the "capturing..." message
      const msgs = this.querySelector('.lmc-messages');
      if (msgs) {
        const last = [...msgs.querySelectorAll('.lmc-message.system')].pop();
        if (last?.textContent.includes('Capturing')) last.remove();
      }
    } catch (err) {
      console.error('[LMC] Screenshot failed:', err);
      this.addSystemMessage('⚠️ Screenshot failed: ' + err.message);
    }
  }

  async _addScreenshotEntry(blob) {
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}${String(now.getSeconds()).padStart(2,'0')}`;
    const name = `screenshot_${ts}.png`;

    const previewUrl = URL.createObjectURL(blob);
    const content = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    this._pendingFiles.push({
      name,
      content,
      mime_type: 'image/png',
      size: blob.size,
      previewUrl,
    });
    this._renderAttachmentBar();
  }

    async _addFiles(fileList) {
    const MAX_FILES = 10;
    const MAX_SIZE_MB = 20;

    for (const file of Array.from(fileList)) {
      if (this._pendingFiles.length >= MAX_FILES) {
        this.addSystemMessage(`⚠️ Max ${MAX_FILES} files per message`);
        break;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        this.addSystemMessage(`⚠️ ${file.name} too large (max ${MAX_SIZE_MB}MB)`);
        continue;
      }

      try {
        const content = await this._fileToBase64(file);
        const entry = {
          name: file.name,
          content,
          mime_type: file.type || 'application/octet-stream',
          size: file.size,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        };
        this._pendingFiles.push(entry);
      } catch (e) {
        this.addSystemMessage(`⚠️ Failed to read ${file.name}`);
      }
    }

    this._renderAttachmentBar();
  }

  _fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // result is "data:mime/type;base64,XXX" — strip the prefix
        const b64 = reader.result.split(',')[1];
        resolve(b64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  _renderAttachmentBar() {
    const bar = this.querySelector('.lmc-attachment-bar');
    if (!bar) return;

    if (this._pendingFiles.length === 0) {
      bar.hidden = true;
      bar.innerHTML = '';
      return;
    }

    bar.hidden = false;
    bar.innerHTML = this._pendingFiles.map((f, i) => {
      if (f.mime_type.startsWith('image/') && f.previewUrl) {
        return `<div class="lmc-att-item lmc-att-image" data-index="${i}">
          <img src="${f.previewUrl}" alt="${this._escapeHtml(f.name)}" />
          <button class="lmc-att-remove" data-index="${i}" title="Remove">✕</button>
        </div>`;
      }
      const icon = this._fileIcon(f.mime_type);
      return `<div class="lmc-att-item lmc-att-doc" data-index="${i}">
        <span class="lmc-att-icon">${icon}</span>
        <span class="lmc-att-name">${this._escapeHtml(f.name)}</span>
        <span class="lmc-att-size">${this._formatSize(f.size)}</span>
        <button class="lmc-att-remove" data-index="${i}" title="Remove">✕</button>
      </div>`;
    }).join('');

    // Remove buttons
    bar.querySelectorAll('.lmc-att-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(btn.dataset.index);
        const removed = this._pendingFiles.splice(idx, 1)[0];
        if (removed && removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
        this._renderAttachmentBar();
      });
    });
  }

  _fileIcon(mimeType) {
    if (!mimeType) return '📄';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📑';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv')) return '📊';
    if (mimeType.startsWith('text/')) return '📄';
    if (mimeType.startsWith('video/')) return '🎬';
    if (mimeType.startsWith('audio/')) return '🎵';
    return '📎';
  }

  _formatSize(bytes) {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }

  // ── Private: Rendering ───────────────────────────────────────

  get _messagesEl() {
    return this.querySelector('.lmc-messages');
  }

  _render() {
    this.innerHTML = `
      <div class="lmc-container">
        <div class="lmc-messages"></div>

        <!-- Attachment preview bar (hidden when empty) -->
        <div class="lmc-attachment-bar" hidden></div>

        <div class="lmc-voice-viz" hidden>
          <div class="lmc-viz-bars">
            <div class="lmc-viz-bar" data-bar="4"></div>
            <div class="lmc-viz-bar" data-bar="3"></div>
            <div class="lmc-viz-bar" data-bar="2"></div>
            <div class="lmc-viz-bar" data-bar="1"></div>
            <div class="lmc-viz-bar" data-bar="0"></div>
            <div class="lmc-viz-bar" data-bar="1"></div>
            <div class="lmc-viz-bar" data-bar="2"></div>
            <div class="lmc-viz-bar" data-bar="3"></div>
            <div class="lmc-viz-bar" data-bar="4"></div>
          </div>
          <div class="lmc-viz-status"></div>
        </div>
        <div class="lmc-input-area" hidden>
          <textarea class="lmc-input" placeholder="Ask your AI assistant..." rows="3"></textarea>
          <div class="lmc-actions">
            <div class="lmc-attach-wrapper">
              <button class="lmc-btn lmc-btn-attach" title="Attach files or screenshot">
                <svg-icon icon="luboo:paperclip"></svg-icon>
              </button>
              <div class="lmc-attach-dropdown" hidden>
                <button class="lmc-attach-opt" data-action="file"><svg-icon icon="luboo:file"></svg-icon> File</button>
                <button class="lmc-attach-opt" data-action="screenshot"><svg-icon icon="luboo:camera"></svg-icon> Screenshot</button>
              </div>
            </div>
            <button class="lmc-btn lmc-btn-ptt" title="Hold to talk">
              <svg-icon icon="luboo:mic"></svg-icon>
            </button>
            <button class="lmc-btn lmc-btn-duplex" title="Voice call">
              <svg-icon icon="luboo:radio"></svg-icon>
            </button>
            <button class="lmc-btn lmc-btn-send" title="Send">
              <svg-icon icon="luboo:send"></svg-icon>
            </button>
          </div>
          <input type="file" class="lmc-file-input" multiple accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx" hidden />
        </div>
      </div>
    `;
    this._showWelcome('AI Assistant', 'Select or create a session above to start chatting.');
  }

  _bindEvents() {
    const input = this.querySelector('.lmc-input');
    if (input) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this._dispatchSend();
          return;
        }
        if (e.key === 'ArrowUp' && this._caretOnFirstLine(input)) {
          if (this._stepHistory(input, -1)) e.preventDefault();
          return;
        }
        if (e.key === 'ArrowDown' && this._caretOnLastLine(input)) {
          if (this._stepHistory(input, 1)) e.preventDefault();
        }
      });
    }

    const sendBtn = this.querySelector('.lmc-btn-send');
    if (sendBtn) {
      sendBtn.addEventListener('click', () => this._dispatchSend());
    }

    // Attach button → dropdown (File or Screenshot)
    const attachBtn = this.querySelector('.lmc-btn-attach');
    const attachDropdown = this.querySelector('.lmc-attach-dropdown');
    const fileInput = this.querySelector('.lmc-file-input');

    if (attachBtn && attachDropdown) {
      attachBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        attachDropdown.hidden = !attachDropdown.hidden;
      });

      // Close dropdown on outside click
      document.addEventListener('click', () => { attachDropdown.hidden = true; });

      attachDropdown.addEventListener('click', async (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        attachDropdown.hidden = true;
        if (action === 'file') {
          fileInput?.click();
        } else if (action === 'screenshot') {
          await this.captureScreenshot();
        }
      });
    }

    if (fileInput) {
      fileInput.addEventListener('change', (e) => {
        if (e.target.files.length) {
          this._addFiles(e.target.files);
          e.target.value = '';
        }
      });
    }

    // ── Full-duplex voice toggle ──────────────────────────────────
    const duplexBtn = this.querySelector('.lmc-btn-duplex');
    if (duplexBtn) {
      duplexBtn.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('chat:voice-toggle', {
          bubbles: true,
        }));
      });
    }

    // ── Hold-to-talk (PTT) ──────────────────────────────────────────
    const pttBtn = this.querySelector('.lmc-btn-ptt');
    if (pttBtn) {
      pttBtn.addEventListener('mousedown', () => this._startRecording());
      pttBtn.addEventListener('mouseup', () => this._stopRecording());
      pttBtn.addEventListener('mouseleave', () => {
        if (this._isRecording) this._stopRecording();
      });
      // Touch support
      pttBtn.addEventListener('touchstart', (e) => { e.preventDefault(); this._startRecording(); });
      pttBtn.addEventListener('touchend', (e) => { e.preventDefault(); this._stopRecording(); });
    }

    // Drag and drop on the container
    const container = this.querySelector('.lmc-container');
    if (container) {
      container.addEventListener('dragover', (e) => {
        e.preventDefault();
        container.classList.add('lmc-drag-over');
      });
      container.addEventListener('dragleave', (e) => {
        if (!container.contains(e.relatedTarget)) {
          container.classList.remove('lmc-drag-over');
        }
      });
      container.addEventListener('drop', (e) => {
        e.preventDefault();
        container.classList.remove('lmc-drag-over');
        const files = e.dataTransfer?.files;
        if (files && files.length) {
          this._addFiles(files);
        }
      });
    }
  }

  _dispatchSend() {
    const text = this.getInputText();
    const files = this._pendingFiles.slice();

    if (!text && files.length === 0) return;

    if (text) this._pushHistory(text);

    // Show user message with file previews immediately
    this.addUserMessage(text, files);
    this.clearInput();

    // Flush files from queue (clears preview bar)
    this.flushPendingFiles();

    this.dispatchEvent(new CustomEvent('chat:message-sent', {
      bubbles: true,
      composed: true,
      detail: { text, files },
    }));
  }

  _onSessionChanged(label) {
    this._loadHistory();
    if (label) {
      this.showInput();
    } else {
      this.showWelcomeState();
    }
  }

  // ── Private: Prompt History (Issue #296) ─────────────────────

  _historyKey() {
    const propertyId = this.getAttribute('property-id');
    const sessionLabel = this.getAttribute('session-label');
    if (!propertyId || !sessionLabel) return null;
    return `lmc-history:${propertyId}:${sessionLabel}`;
  }

  _loadHistory() {
    this._historyIndex = null;
    this._draftBeforeHistory = '';
    const key = this._historyKey();
    if (!key || typeof localStorage === 'undefined') {
      this._messageHistory = [];
      return;
    }
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      this._messageHistory = Array.isArray(parsed)
        ? parsed.filter((s) => typeof s === 'string')
        : [];
    } catch {
      this._messageHistory = [];
    }
  }

  _saveHistory() {
    const key = this._historyKey();
    if (!key || typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(key, JSON.stringify(this._messageHistory));
    } catch {
      // quota exceeded / storage disabled — degrade silently to in-memory
    }
  }

  _pushHistory(text) {
    const HISTORY_MAX = 50;
    const last = this._messageHistory[this._messageHistory.length - 1];
    if (last === text) {
      this._historyIndex = null;
      this._draftBeforeHistory = '';
      return;
    }
    this._messageHistory.push(text);
    if (this._messageHistory.length > HISTORY_MAX) {
      this._messageHistory.splice(0, this._messageHistory.length - HISTORY_MAX);
    }
    this._saveHistory();
    this._historyIndex = null;
    this._draftBeforeHistory = '';
  }

  _caretOnFirstLine(input) {
    return !input.value.substring(0, input.selectionStart).includes('\n');
  }

  _caretOnLastLine(input) {
    return !input.value.substring(input.selectionEnd).includes('\n');
  }

  /**
   * Navigate history. delta = -1 (Up, older) or +1 (Down, newer/draft).
   * Returns true if the keystroke was handled (caller should preventDefault).
   */
  _stepHistory(input, delta) {
    const history = this._messageHistory;
    if (!history.length) return false;

    if (delta < 0) {
      if (this._historyIndex === null) {
        this._draftBeforeHistory = input.value;
        this._historyIndex = history.length - 1;
      } else if (this._historyIndex > 0) {
        this._historyIndex -= 1;
      } else {
        return false;
      }
      input.value = history[this._historyIndex];
    } else {
      if (this._historyIndex === null) return false;
      if (this._historyIndex < history.length - 1) {
        this._historyIndex += 1;
        input.value = history[this._historyIndex];
      } else {
        this._historyIndex = null;
        input.value = this._draftBeforeHistory;
        this._draftBeforeHistory = '';
      }
    }
    input.setSelectionRange(input.value.length, input.value.length);
    return true;
  }

  _showWelcome(title, subtitle) {
    const container = this._messagesEl;
    if (!container) return;
    const t = title || 'AI Assistant';
    const s = subtitle || 'Start a conversation...';
    container.innerHTML = `
      <div class="lmc-welcome luboo-empty-state">
        <svg-icon icon="luboo:sparkle"></svg-icon>
        <h3>${t}</h3>
        <p>${s}</p>
      </div>
    `;
  }

  _hideInput() {
    const inputArea = this.querySelector('.lmc-input-area');
    if (inputArea) inputArea.hidden = true;
  }

  // ── Hold-to-talk Recording ────────────────────────────────────────

  async _startRecording() {
    if (this._isRecording) return;
    this._isRecording = true;
    this._audioChunks = [];

    const pttBtn = this.querySelector('.lmc-btn-ptt');
    if (pttBtn) pttBtn.classList.add('recording');

    // Show viz bar above input
    const voiceViz = this.querySelector('.lmc-voice-viz');
    const vizStatus = this.querySelector('.lmc-viz-status');
    if (voiceViz) voiceViz.hidden = false;
    if (vizStatus) { vizStatus.textContent = '🎤 Recording...'; vizStatus.className = 'lmc-viz-status'; }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this._mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });

      this._mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this._audioChunks.push(e.data);
      };

      this._mediaRecorder.start();

      // Real AudioContext analyser — same pattern as full-duplex
      this._pttAudioCtx = new AudioContext();
      const source = this._pttAudioCtx.createMediaStreamSource(stream);
      this._pttAnalyser = this._pttAudioCtx.createAnalyser();
      this._pttAnalyser.fftSize = 32;
      source.connect(this._pttAnalyser);
      this._startPttVisualization();
    } catch (err) {
      console.error('[LMC] Mic access denied:', err);
      this._isRecording = false;
      if (pttBtn) pttBtn.classList.remove('recording');
      const voiceViz2 = this.querySelector('.lmc-voice-viz');
      if (voiceViz2) voiceViz2.hidden = true;
      this.addSystemMessage('Microphone access denied. Please allow mic access and try again.');
    }
  }

  _startPttVisualization() {
    const allBars = this.querySelectorAll('.lmc-viz-bars .lmc-viz-bar');
    if (!allBars.length || !this._pttAnalyser) return;
    const data = new Uint8Array(this._pttAnalyser.frequencyBinCount);
    const update = () => {
      if (!this._isRecording) return;
      this._pttAnalyser.getByteFrequencyData(data);
      const binCount = data.length;
      const binsPerBar = Math.max(1, Math.floor(binCount / 5));
      const heights = [];
      for (let i = 0; i < 5; i++) {
        let sum = 0;
        for (let j = i * binsPerBar; j < (i + 1) * binsPerBar && j < binCount; j++) sum += data[j];
        heights.push(4 + (sum / binsPerBar / 255) * 28);
      }
      allBars.forEach(bar => {
        const idx = parseInt(bar.dataset.bar);
        if (!isNaN(idx) && idx < heights.length) bar.style.height = `${heights[idx]}px`;
      });
      this._pttVizFrame = requestAnimationFrame(update);
    };
    this._pttVizFrame = requestAnimationFrame(update);
  }

  _stopPttVisualization() {
    if (this._pttVizFrame) { cancelAnimationFrame(this._pttVizFrame); this._pttVizFrame = null; }
    if (this._pttAnalyser) { this._pttAnalyser = null; }
    if (this._pttAudioCtx) { this._pttAudioCtx.close(); this._pttAudioCtx = null; }
    // Reset bars to resting height
    this.querySelectorAll('.lmc-viz-bars .lmc-viz-bar').forEach(b => b.style.height = '4px');
  }

  _stopRecording() {
    if (!this._isRecording || !this._mediaRecorder) return;
    this._isRecording = false;

    const pttBtn = this.querySelector('.lmc-btn-ptt');
    if (pttBtn) pttBtn.classList.remove('recording');

    this._stopPttVisualization();
    const vizStatus = this.querySelector('.lmc-viz-status');
    if (vizStatus) { vizStatus.textContent = '🎤 Transcribing...'; vizStatus.className = 'lmc-viz-status interim'; }

    this._mediaRecorder.stop();

    this._mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(this._audioChunks, { type: 'audio/webm' });

      if (audioBlob.size < 1000) {
        const voiceViz = this.querySelector('.lmc-voice-viz');
        if (voiceViz) voiceViz.hidden = true;
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        this.dispatchEvent(new CustomEvent('chat:audio-sent', {
          bubbles: true,
          detail: { data: base64 },
        }));
      };
      reader.readAsDataURL(audioBlob);

      if (this._mediaRecorder.stream) {
        this._mediaRecorder.stream.getTracks().forEach(t => t.stop());
      }
    };
  }

  _renderMarkdown(text) {
    return marked.parse(text, { breaks: true });
  }

  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

export { LunariaManagementChat };
